// Client-side exports for Razorpay integration
export const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_live_6QAKAeBfeRhFfn';

import { supabase } from './supabase';

// Declare Razorpay global type
declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export const validatePromoCode = async (
  promoCode: string,
  context: 'personalized_list' | 'student_connect' = 'personalized_list'
): Promise<{ isValid: boolean; discount: number }> => {
  if (!promoCode.trim()) return { isValid: false, discount: 0 };
  try {
    const { data, error } = await supabase
      .from('promo_codes')
      .select('discount, applies_to, is_active, max_uses, used_count, expires_at')
      .eq('code', promoCode.toUpperCase().trim())
      .eq('is_active', true)
      .maybeSingle();

    if (error || !data) return { isValid: false, discount: 0 };

    // Check applies_to scope
    if (data.applies_to !== 'both' && data.applies_to !== context) return { isValid: false, discount: 0 };

    // Check usage limit
    if (data.max_uses !== null && data.used_count >= data.max_uses) return { isValid: false, discount: 0 };

    // Check expiry
    if (data.expires_at && new Date(data.expires_at) < new Date()) return { isValid: false, discount: 0 };

    return { isValid: true, discount: data.discount };
  } catch {
    return { isValid: false, discount: 0 };
  }
};

export const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Create Razorpay order using Supabase Edge Function
export const createRazorpayOrder = async (amount: number) => {
  try {
    console.log('🚀 Creating Razorpay order for amount:', amount);
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl) {
      console.error('❌ Supabase URL missing');
      throw new Error('Payment system not configured. Please contact support.');
    }
    
    if (!supabaseKey) {
      console.error('❌ Supabase key missing');
      throw new Error('Payment system not configured. Please contact support.');
    }

    console.log('📡 Making request to:', `${supabaseUrl}/functions/v1/create-order`);
    console.log('💰 Amount:', amount, 'INR');

    const response = await fetch(`${supabaseUrl}/functions/v1/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({
        amount: amount,
        currency: 'INR'
      }),
    });

    console.log('📊 Response status:', response.status);
    console.log('📋 Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Order creation failed:', errorText);
      
      let errorMessage = 'Failed to create payment order';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.details || errorData.error || errorMessage;
      } catch (e) {
        errorMessage = `HTTP ${response.status}: ${errorText}`;
      }
      
      throw new Error(errorMessage);
    }

    const order = await response.json();
    console.log('✅ Order created successfully:', order);
    return order;
  } catch (error: any) {
    console.error('❌ Error creating order:', error);
    
    // Provide more specific error messages
    if (error.message?.includes('Failed to fetch')) {
      throw new Error('🌐 Network error: Unable to connect to payment server. Please check your internet connection and try again.');
    } else if (error.message?.includes('not configured')) {
      throw new Error('⚙️ Payment system configuration error. Please contact support.');
    } else {
      throw new Error(error.message || 'Failed to create payment order. Please try again.');
    }
  }
};

// Verify payment using Supabase Edge Function
export const verifyPayment = async (
  orderId: string,
  paymentId: string,
  signature: string,
  submissionId: string
): Promise<{ success: boolean }> => {
  try {
    console.log('🔍 Verifying payment:', { orderId, paymentId, signature: signature ? 'present' : 'missing', submissionId });
    
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Supabase configuration missing');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({
        razorpay_order_id: orderId,
        razorpay_payment_id: paymentId,
        razorpay_signature: signature,
        submission_id: submissionId
      }),
    });

    const result = await response.json();
    console.log('✅ Payment verification result:', result);
    
    return { success: result.success };
  } catch (error) {
    console.error('❌ Error verifying payment:', error);
    return { success: false };
  }
};

// Debug function to test Razorpay connection
export const debugRazorpayConnection = async () => {
  try {
    console.log('🔧 === RAZORPAY DEBUG TEST ===');
    console.log('Environment Check:');
    console.log('- Supabase URL:', import.meta.env.VITE_SUPABASE_URL ? '✅ Set' : '❌ Missing');
    console.log('- Supabase Anon Key:', import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Set' : '❌ Missing');
    console.log('- Razorpay Key ID:', RAZORPAY_KEY_ID ? '✅ Set' : '❌ Missing');
    
    console.log('\nScript Loading Test:');
    const scriptLoaded = await loadRazorpayScript();
    console.log('- Razorpay Script:', scriptLoaded ? '✅ Loaded' : '❌ Failed');
    console.log('- Window.Razorpay:', window.Razorpay ? '✅ Available' : '❌ Not Available');
    
    console.log('\nOrder Creation Test:');
    try {
      const testOrder = await createRazorpayOrder(299);
      console.log('- Test Order:', testOrder ? '✅ Created' : '❌ Failed');
      console.log('- Order Details:', testOrder);
    } catch (orderError) {
      console.log('- Test Order: ❌ Failed');
      console.error('❌ Order Error:', orderError);
    }
    
    console.log('\n🏁 === DEBUG TEST COMPLETE ===');
    
    return {
      success: true,
      message: '🎉 Razorpay connection test completed - check console for details',
      environment: {
        supabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
        supabaseKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
        razorpayKey: !!RAZORPAY_KEY_ID,
        scriptLoaded,
        razorpayAvailable: !!window.Razorpay
      }
    };
  } catch (error: any) {
    console.error('❌ Debug test failed:', error);
    return {
      success: false,
      error: error.message,
      message: '❌ Debug test failed - check console for details'
    };
  }
};